import { useEffect, useId, useMemo, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Icon } from '../ui/Icon.tsx';
import { cx } from '../ui/cx.ts';
import { Modal } from './Modal.tsx';

/**
 * ⌘K command palette primitive — source-agnostic and fully controlled;
 * `ShellCommandPalette` supplies the groups.
 *
 * Built on the local `Modal` (`palette` variant) so escape-to-close,
 * backdrop-to-close and the focus trap come from the one implementation
 * the rest of the app already trusts, rather than a second copy.
 *
 * Keyboard model is the ARIA combobox pattern, NOT a roving tabindex:
 * focus stays in the text box the whole time and `aria-activedescendant`
 * points at the highlighted option. That is what lets a tenant type and
 * steer in one motion — Tab never has to visit eleven nav rows.
 */
export interface CommandItem {
  readonly id: string;
  readonly label: string;
  /** Right-aligned mono annotation — a path, or the entity kind. */
  readonly hint?: string;
  readonly onSelect: () => void;
}

export interface CommandGroup {
  /** Stable slug; also forms the group's test id. */
  readonly id: string;
  readonly heading: string;
  readonly items: readonly CommandItem[];
}

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly groups: readonly CommandGroup[];
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly placeholder: string;
  readonly emptyText: ReactNode;
  readonly ariaLabel: string;
  readonly footer?: ReactNode;
  readonly testId: string;
}

export function CommandPalette({
  open,
  onOpenChange,
  groups,
  search,
  onSearchChange,
  placeholder,
  emptyText,
  ariaLabel,
  footer,
  testId,
}: Props): JSX.Element | null {
  const domId = useId();
  const listId = `${domId}-list`;
  const titleId = `${domId}-title`;
  const [activeIndex, setActiveIndex] = useState(0);

  const flatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  // Any change to the result set re-aims at the first row: after typing
  // one more character, "the third result" means something different.
  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems]);

  if (!open) {
    return null;
  }

  const activeItem = flatItems[activeIndex];
  const activeId = activeItem !== undefined ? `${domId}-opt-${activeItem.id}` : undefined;

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (flatItems.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      flatItems[activeIndex]?.onSelect();
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      titleId={titleId}
      testId={testId}
      variant="palette"
    >
      <h2 id={titleId} className="sr-only">
        {ariaLabel}
      </h2>
      <div className="flex items-center gap-2.5 border-b border-line-soft px-4">
        <Icon name="search" size="sm" className="text-ink-low" />
        <input
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          autoComplete="off"
          data-testid={`${testId}-search`}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-12 w-full bg-transparent text-body text-ink outline-none placeholder:text-ink-faint"
        />
      </div>
      <div
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        className="max-h-[360px] overflow-y-auto overscroll-contain p-2"
      >
        {flatItems.length === 0 ? (
          <p
            data-testid={`${testId}-empty`}
            className="px-3 py-8 text-center text-caption text-ink-low"
          >
            {emptyText}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.id} data-testid={`${testId}-group-${group.id}`} role="group">
              <p className="px-2.5 pb-1 pt-2 text-mono-label uppercase text-ink-low">
                {group.heading}
              </p>
              {group.items.map((item) => {
                const selected = item.id === activeItem?.id;
                return (
                  <div
                    key={item.id}
                    id={`${domId}-opt-${item.id}`}
                    role="option"
                    aria-selected={selected}
                    onClick={item.onSelect}
                    onMouseEnter={() =>
                      setActiveIndex(flatItems.findIndex((candidate) => candidate.id === item.id))
                    }
                    className={cx(
                      'flex cursor-pointer select-none items-center justify-between gap-3 rounded px-2.5 py-2 text-label transition-colors duration-150',
                      selected ? 'bg-active text-ink' : 'text-ink-mid',
                    )}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.hint !== undefined ? (
                      <span className="shrink-0 font-mono text-caption text-ink-low">
                        {item.hint}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
      {footer !== undefined ? (
        <div className="border-t border-line-soft px-4 py-2.5 text-caption text-ink-low">
          {footer}
        </div>
      ) : null}
    </Modal>
  );
}
